import React, { useState, useEffect, useContext } from "react";
import jsPDF from "jspdf";
import { UserContext } from "../../UserContext";
import { useNavigate, useLocation } from "react-router-dom";

const Downloadchat = () => {
  const [sessions, setSessions] = useState([]);  // eslint-disable-line no-unused-vars
  const [groupedSessions, setGroupedSessions] = useState({});
  const [selectedSessionId, setSelectedSessionId] = useState(null);
  const [coachName, setCoachName] = useState(null);
  const [chatData, setChatData] = useState(null);
  const [chatDate, setChatDate] = useState("");
  const [isDisabled, setIsDisabled] = useState(false);
  const navigate = useNavigate();
  const location = useLocation();
  const { user } = useContext(UserContext);
  const apiBaseURL = process.env.REACT_APP_BACKEND_API_URL;

  // Helper function to group sessions by date
  const groupSessionsByDate = (sessions) => {
    return sessions.reduce((acc, session) => {
      const date = new Date(session.created_at).toDateString(); // Get date string (e.g., "Tue Nov 14 2024")
      if (!acc[date]) acc[date] = [];
      acc[date].push(session);
      return acc;
    }, {});
  };

  useEffect(() => {
    const fetchSessions = async () => {
      if (!user.user_id) {
        navigate("/signUp", { state: { from: location.pathname } });
      } else {
        try {
          const response = await fetch(
            `${apiBaseURL}/users/${user.user_id}/allsessions`
          );

          if (response.ok) {
            const data = await response.json();
            // console.log(data);
            const sortedSessions = data.sort(
              (a, b) => new Date(b.created_at) - new Date(a.created_at)
            );
            // console.log(sortedSessions);
            setSessions(sortedSessions);
            setGroupedSessions(groupSessionsByDate(sortedSessions));
          } else {
            throw Error("Error in data fetching");
          }
        } catch (error) {
          console.error("Error fetching sessions:", error);
        }
      }
    };

    fetchSessions();
  }, [user, navigate, apiBaseURL, location.pathname]);

  useEffect(() => {
    const fetchChatData = async () => {
      if (!selectedSessionId) return;
      try {
        const response = await fetch(
          `${apiBaseURL}/sessionSteps/${selectedSessionId}`
        );
        if (response.ok) {
          const data = await response.json();
          // console.log(data);
          setChatData(data);
          const date = new Date(data[0].created_at).toDateString();
          setChatDate(date);
        } else {
          console.error("Failed to fetch chat data:", response.statusText);
        }
      } catch (error) {
        console.error("Error fetching chat data:", error);
      }
    };

    fetchChatData();
  }, [selectedSessionId, apiBaseURL]);

  const continuePreviousConversation = async () => {
    try {
      await fetch(`${apiBaseURL}/users/${user.user_id}/session/end`, {
        method: "PUT",
        headers: {
          "Content-Type": "application/json",
        },
      });
      // const exitSession = await res.json();
      // console.log(exitSession);
      console.log("Session Ended");
    } catch (error) {
      console.log("The session has ended:", error.message);
    }
    try {
      // const res = await fetch(
      //   `${apiBaseURL}/users/Previous_session/${selectedSessionId}`,
      //   {
      //     method: "PUT",
      //     headers: {
      //       "Content-Type": "application/json",
      //     },
      //   }
      // );

      await fetch(`${apiBaseURL}/users/Previous_session/${selectedSessionId}`, {
        method: "PUT",
        headers: {
          "Content-Type": "application/json",
        },
      });

      // if (!res.ok) {
      //   // Log the error status and response body
      //   const errorData = await res.json();
      //   console.error("Failed to extend session:", errorData);
      //   throw new Error(errorData.detail || "Unknown error occurred");
      // }

      // const exitSession = await res.json();
      console.log("contenue session");
      setIsDisabled(false);
      navigate("/chat");
    } catch (error) {
      console.log("The session has ended:", error.message);
      navigate("/");
    }
  };

  const downloadSessionAsPDF = () => {
    const doc = new jsPDF();
    const pageHeight = 297; // A4 page height in mm
    const margin = 10;
    const usableHeight = pageHeight - 2 * margin;
    const lineHeight = 10;
    const coachMessageIndent = 40; // Set margin for the coach's message

    let yPosition = 20; // Start position
    doc.setFontSize(16);
    doc.text(
      `Chat History - ${user.firstName} ${user.lastName}`,
      margin,
      yPosition
    );
    yPosition += 10;

    chatData.forEach((message) => {
      const userText = `${user.firstName}: ${message.user_message}`;
      const userTextLines = doc.splitTextToSize(userText, 180 - margin);

      userTextLines.forEach((line) => {
        if (yPosition + lineHeight > usableHeight) {
          doc.addPage(); // Add a new page if needed
          yPosition = margin;
        }
        doc.setFontSize(12);
        doc.text(line, margin, yPosition);
        yPosition += lineHeight;
      });

      yPosition += 5;

      const botText = `${coachName}: ${message.bot_response}`;
      const botTextLines = doc.splitTextToSize(
        botText,
        180 - coachMessageIndent
      );

      botTextLines.forEach((line) => {
        if (yPosition + lineHeight > usableHeight) {
          doc.addPage();
          yPosition = margin;
        }
        doc.setFontSize(12);
        doc.text(line, coachMessageIndent, yPosition);
        yPosition += lineHeight;
      });

      yPosition += 10;
    });

    doc.save(`session-${selectedSessionId}.pdf`);
  };

  return (
    <div className="p-10 w-full h-full flex items-start justify-center font-serif">
      <div className="flex flex-col md:flex-row w-full max-w-7xl bg-white rounded-2xl shadow-xl overflow-hidden border border-[#e5e7eb] h-[75vh]">
        {/* Left Sidebar (Session List) */}
        <div
          className={`md:w-[30%] ${
            selectedSessionId ? "hidden md:flex" : "flex"
          } flex-col bg-gradient-to-br from-blue-200 via-gray-50 to-blue-200 p-6 border-r border-[#e5e7eb] overflow-y-auto`}
        >
          <h2 className="text-lg font-semibold text-[#333333] mb-4 text-center">
            Chat Sessions
          </h2>
          <div className="space-y-3">
            {Object.keys(groupedSessions).length > 0 ? (
              Object.entries(groupedSessions).map(([date, sessions]) => (
                <div key={date}>
                  <h3 className="text-sm text-[#616161]">{date}</h3>
                  {sessions.map((session) => (
                    <div
                      key={session.session_id}
                      onClick={() => {
                        setSelectedSessionId(session.session_id);
                        setCoachName(session.coach_name);
                      }}
                      className={`p-3 rounded-lg cursor-pointer m-2 ${
                        selectedSessionId === session.session_id
                          ? "bg-white"
                          : "bg-gray-200"
                      } hover:bg-blue-200 transition duration-200 shadow-sm border border-[#e5e7eb]`}
                    >
                      <p className="text-sm font-medium text-[#4b5563]">
                        {session.coachee_response}
                      </p>
                    </div>
                  ))}
                </div>
              ))
            ) : (
              <p className="text-sm text-[#6b7280] text-center">
                No sessions available
              </p>
            )}
          </div>
        </div>

        {/* Right Main Chat Window */}
        <div
          className={`flex flex-col w-full md:w-[70%] h-full bg-[#f9fafb] p-6 ${
            selectedSessionId ? "flex" : "hidden md:flex"
          }`}
        >
          {selectedSessionId && (
            <button
              className="md:hidden mb-4 text-[#54B0AF] hover:text-teal-600 font-semibold"
              onClick={() => setSelectedSessionId(null)}
            >
              &larr; Back to Sessions
            </button>
          )}

          <div className="flex justify-between items-center mb-4">
            <h2 className="text-lg font-bold text-gray-700">
              {selectedSessionId
                ? `Chat with ${coachName} on ${chatDate}`
                : "Select a Session"}
            </h2>
            {selectedSessionId && chatData && (
              <div className="flex space-x-2">
                <button
                  className="bg-gray-200 text-black font-medium font-gotham py-1.5 px-4 rounded-full hover:opacity-90 transition duration-300"
                  onClick={() => {
                    setIsDisabled(true);
                    continuePreviousConversation();
                  }}
                  disabled={isDisabled}
                >
                  Continue Chat
                </button>
                <button
                  className="bg-gray-200 text-black font-medium font-gotham py-1.5 px-4 rounded-full hover:opacity-90 transition duration-300"
                  onClick={downloadSessionAsPDF}
                >
                  Download ⬇
                </button>
              </div>
            )}
          </div>

          {chatData && selectedSessionId ? (
            <div className="flex flex-col space-y-4 overflow-y-auto h-full p-4 bg-[#ffffff] rounded-lg shadow-inner">
              {chatData.map((message, index) => (
                <div key={index} className="flex flex-col space-y-2">
                  <div className="flex justify-start">
                    <div className="max-w-[75%] p-3 bg-blue-100 text-black rounded-lg rounded-tl-none shadow-sm">
                      <p className="text-sm md:text-base">
                        {message.coachee_response}
                      </p>
                    </div>
                  </div>
                  <div className="flex justify-end">
                    <div className="max-w-[75%] p-3 bg-[#f1f5f9] text-black rounded-lg rounded-tr-none shadow-sm">
                      <p className="text-sm md:text-base">
                        {message.coach_response}
                      </p>
                    </div>
                  </div>
                </div>
              ))}
            </div>
          ) : (
            <div className="flex flex-col justify-center items-center h-full text-[#6b7280]">
              <p>Select a session to view messages</p>
            </div>
          )}
        </div>
      </div>
    </div>
  );
};

export default Downloadchat;
